/**
 * The dealer's Scheduled Reports page — the sidebar entry that stood as a
 * placeholder from the first commit until the revenue-assurance plan gave it
 * something to hold (Ian, 2026-10-07: "there is a scheduled reports that
 * exists inside for the dealer"). Its first section is the morning
 * inventory-audit alerts: who gets emailed about which problems, every
 * morning, for the active dealer group. The audit page's Alerts button
 * deep-links here. Not to be confused with INVI HQ's own Scheduled Reports
 * page, which only the platform operator sees.
 *
 * Dolan's verdict (2026-10-06) was that staff will not learn a new tool, so
 * the alert is the product and this page only routes it: pick the problems,
 * name the people, choose the time. The server decides when an email goes out
 * — only off an audit run newer than the last email — and this page says so.
 */
import React, { useCallback, useEffect, useState } from "react"

import {
  AuditAlert,
  AuditAlertInput,
  AuditAlertProblemOption,
  createAuditAlert,
  deleteAuditAlert,
  fetchAlertProblems,
  fetchAuditAlerts,
  sendAuditAlertNow,
  updateAuditAlert,
} from "../../api/auditAlerts"
import { AuditRooftopOption, listAuditRooftops } from "../../api/audit"
import AuditAlertForm from "../../components/audit/alerts/AuditAlertForm"
import AuditAlertsTable from "../../components/audit/alerts/AuditAlertsTable"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import Layout from "../../components/Layout"
import { THEME } from "../../config/theme"
import { useDealerGroup } from "../../context/DealerGroupContext"

type FormState = { mode: "closed" } | { mode: "new" } | { mode: "edit"; alert: AuditAlert }

// Brand primary button — components/audit/DownloadMenu.tsx:146-163 (buttonStyle).
const primaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  backgroundColor: THEME.colors.navy,
  color: THEME.colors.white,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
}

// Collapsible utility panel — components/photos/FileRequirementsPanel.tsx ~1042
// (silver border, md radius, white surface).
const panelStyle: React.CSSProperties = {
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.white,
  marginBottom: THEME.spacing.lg,
  overflow: "hidden",
}

const panelHeadStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: THEME.spacing.sm,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  fontSize: 15,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

export default function DealerScheduledReportsPage() {
  return (
    <Layout>
      <DealerScheduledReportsContent />
    </Layout>
  )
}

export function DealerScheduledReportsContent() {
  const { dealerGroupId } = useDealerGroup()
  const [options, setOptions] = useState<AuditAlertProblemOption[]>([])
  // The group's stores for the form's Store choice — the audit page's own list.
  const [stores, setStores] = useState<AuditRooftopOption[]>([])
  const [alerts, setAlerts] = useState<AuditAlert[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>({ mode: "closed" })
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [sendingId, setSendingId] = useState<number | null>(null)
  // What the last Send now did, in words: an alert with nothing fresh sends
  // nothing, and the page must say that rather than claim it sent something.
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(() => {
    if (dealerGroupId === null) return undefined
    let cancelled = false
    setLoadError(null)
    Promise.all([
      fetchAlertProblems(),
      fetchAuditAlerts(dealerGroupId),
      // No store list is not a failure: the form then offers the whole group only.
      listAuditRooftops(dealerGroupId).then((response) => response.rooftops ?? []).catch(() => []),
    ])
      .then(([nextOptions, nextAlerts, nextStores]) => {
        if (cancelled) return
        setOptions(nextOptions)
        setAlerts(nextAlerts)
        setStores(nextStores)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load alerts")
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  // Reload when the active group changes; anything being edited belonged to
  // the old group, so the form closes with it.
  useEffect(() => {
    setAlerts(null)
    setForm({ mode: "closed" })
    setNotice(null)
    return load()
  }, [load])

  if (dealerGroupId === null) {
    return <DealerGroupRequiredNotice surface="scheduled reports" />
  }

  const save = async (input: AuditAlertInput) => {
    setSaving(true)
    setFormError(null)
    try {
      const saved = form.mode === "edit"
        ? await updateAuditAlert(form.alert.id, input)
        : await createAuditAlert({ ...input, dealer_group_id: dealerGroupId })
      setAlerts((current) => {
        const rest = (current ?? []).filter((alert) => alert.id !== saved.id)
        return [...rest, saved].sort((a, b) => a.id - b.id)
      })
      setForm({ mode: "closed" })
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to save the alert")
    } finally {
      setSaving(false)
    }
  }

  const remove = async (alert: AuditAlert) => {
    try {
      await deleteAuditAlert(alert.id)
      setAlerts((current) => (current ?? []).filter((candidate) => candidate.id !== alert.id))
      setNotice(`Deleted “${alert.name}”.`)
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to delete the alert")
    }
  }

  const toggleEnabled = async (alert: AuditAlert) => {
    try {
      const saved = await updateAuditAlert(alert.id, { is_enabled: !alert.is_enabled })
      setAlerts((current) => (current ?? []).map((candidate) => (candidate.id === saved.id ? saved : candidate)))
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to update the alert")
    }
  }

  const sendNow = async (alert: AuditAlert) => {
    setSendingId(alert.id)
    setNotice(null)
    try {
      const sent = await sendAuditAlertNow(alert.id)
      setNotice(
        sent
          ? `Sent “${alert.name}” to ${alert.recipients.length} ${alert.recipients.length === 1 ? "person" : "people"}.`
          : `Nothing fresh to send for “${alert.name}” — no audit (inventory and website) has finished since its last email.`,
      )
      if (sent) {
        setAlerts((current) =>
          (current ?? []).map((candidate) =>
            candidate.id === alert.id ? { ...candidate, last_sent_at: new Date().toISOString() } : candidate,
          ),
        )
      }
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to send the alert")
    } finally {
      setSendingId(null)
    }
  }

  return (
    <div>
      {/* Page header bar — pages/photos/DailyPhotoListPage.tsx ~318-369. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: THEME.spacing.md }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.5px", margin: 0, color: THEME.colors.neutral.light.textPrimary }}>
            Scheduled Reports
          </h1>
          <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary, marginTop: 4, maxWidth: "70ch" }}>
            The emails INVI sends your team on a schedule. Morning audit alerts: who gets emailed about
            which problems, every morning. An alert goes out only when a fresh audit has run since the
            last one.
          </div>
        </div>
        {form.mode === "closed" && (
          <button type="button" style={primaryButtonStyle} onClick={() => { setFormError(null); setForm({ mode: "new" }) }}>
            + New alert
          </button>
        )}
      </div>

      {notice && (
        <div
          role="status"
          style={{
            border: `1px solid ${THEME.colors.silver}`,
            borderRadius: THEME.borderRadius.md,
            padding: "10px 14px",
            marginBottom: THEME.spacing.md,
            fontSize: 13,
            background: THEME.colors.white,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          {notice}
        </div>
      )}

      {form.mode !== "closed" && (
        <div style={panelStyle}>
          <div style={panelHeadStyle}>
            <span>{form.mode === "edit" ? `Edit “${form.alert.name}”` : "New alert"}</span>
            <span style={{ fontSize: 12.5, fontWeight: 400, color: THEME.colors.neutral.light.textSecondary }}>
              Pick the problems, add the people, choose the time.
            </span>
          </div>
          <AuditAlertForm
            // Remount per alert so a half-edited form never carries into another.
            key={form.mode === "edit" ? form.alert.id : "new"}
            options={options}
            stores={stores}
            initial={form.mode === "edit" ? form.alert : null}
            saving={saving}
            error={formError}
            onSave={save}
            onCancel={() => setForm({ mode: "closed" })}
          />
        </div>
      )}

      <div style={panelStyle}>
        <div style={panelHeadStyle}>
          <span>Morning audit alerts</span>
          {alerts !== null && (
            <span style={{ fontSize: 12.5, fontWeight: 400, color: THEME.colors.neutral.light.textSecondary }}>
              {alerts.length} {alerts.length === 1 ? "alert" : "alerts"}
            </span>
          )}
        </div>
        {loadError !== null ? (
          <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.semantic.danger }}>{loadError}</div>
        ) : alerts === null ? (
          <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>Loading…</div>
        ) : (
          <AuditAlertsTable
            alerts={alerts}
            options={options}
            sendingId={sendingId}
            onEdit={(alert) => { setFormError(null); setForm({ mode: "edit", alert }) }}
            onDelete={remove}
            onSendNow={sendNow}
            onToggleEnabled={toggleEnabled}
          />
        )}
      </div>
    </div>
  )
}
